/**
 * Coordinated logout hook that handles:
 * 1. Parallel sign-out: Backend, Cognito, and Privy (all independent)
 * 2. Local state reset (Zustand, Sentry)
 *
 * Uses Promise.all for parallel execution to ensure Cognito tokens
 * are cleared immediately, preventing auto-re-authentication.
 */

import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { signOut } from 'aws-amplify/auth';
import { useCallback, useState } from 'react';
import { type NavigateFunction, useNavigate } from 'react-router-dom';

import { apiUserFetch, resetPermRevTracking } from '@/api';
import { endAuthSession } from '@/features/auth/auth-session';
import { cancelSignIn } from '@/features/auth/cognito-sign-in';
import { suppressPasskeyPrompt } from '@/features/auth/passkey-login-preference';
import { clearPrivySession, type PrivySession } from '@/features/wallet-access/privy-session';
import { usePrivySession } from '@/features/wallet-access/use-privy-session';
import { walletSecurityReminder } from '@/features/wallet-access/wallet-security-reminder-store';
import { walletUnlockSession } from '@/features/wallet-access/wallet-unlock-session';
import { PagePaths } from '@/routes/page-paths';
import { useToast } from '@/shared/design-system';
import { useSelectedUserConfigSlice } from '@/stores/selectors';
import { setSentryUser } from '@/utils/sentry/sentry-user';

export type LogoutMode = 'normal' | 'reauth' | 'expired';

const EXPIRED_SESSION_NOTICE = 'Your session has expired. Please sign in again.';

export type LogoutResult =
  | { status: 'complete'; backendSignedOut: boolean }
  | { status: 'incomplete'; backendSignedOut: false }
  | { status: 'in-progress'; backendSignedOut: false };

interface UseLogoutReturn {
  logout: (_options?: { mode?: LogoutMode }) => Promise<LogoutResult>;
  isLoggingOut: boolean;
}

const clearLocalSession = async (queryClient: QueryClient, resetState: () => void) => {
  await walletSecurityReminder.reset();
  resetState();
  setSentryUser(null);
  // The next session's `x-perm-rev` has nothing to do with this one's —
  // don't compare across the sign-out boundary (GP-3853 Task 11).
  resetPermRevTracking();
  endAuthSession(queryClient);
};

type AddToast = ReturnType<typeof useToast>['add'];

const IN_PROGRESS: LogoutResult = { status: 'in-progress', backendSignedOut: false };
const INCOMPLETE: LogoutResult = { status: 'incomplete', backendSignedOut: false };

const settleSignOut = (provider: string, signOutRequest: Promise<unknown>): Promise<boolean> =>
  signOutRequest.then(
    () => {
      console.log(`[Logout] ${provider} sign-out successful`);
      return true;
    },
    () => {
      console.warn(`[Logout] ${provider} sign-out failed`);
      return false;
    },
  );

const signOutEverywhere = async (privySession: PrivySession): Promise<boolean> => {
  console.log('[Logout] Running parallel sign-out (Backend, Cognito, Privy)');
  const [backendSignedOut] = await Promise.all([
    // Clears the server session cookie.
    settleSignOut('Backend', apiUserFetch('/auth/sign-out', { method: 'POST' })),
    // Clears Amplify tokens so the session cannot re-authenticate itself.
    settleSignOut('Cognito', signOut()),
    settleSignOut('Privy', clearPrivySession(privySession)),
  ]);
  return backendSignedOut;
};

const beginLogout = () => {
  console.log('[Logout] Starting logout process...');
  walletUnlockSession.lock();
  suppressPasskeyPrompt();
  cancelSignIn();
};

const failLogout = (mode: LogoutMode, addToast: AddToast): LogoutResult => {
  if (mode === 'normal') {
    addToast({
      title: 'Could not sign out',
      description: 'Your Gamma session is still active. Try again.',
      data: { status: 'error' },
    });
  }

  return INCOMPLETE;
};

const leaveAfterLogout = (mode: LogoutMode, navigate: NavigateFunction) => {
  if (mode === 'normal') {
    navigate(PagePaths.root, { replace: true });
  }

  if (mode === 'expired') {
    navigate(PagePaths.root, { replace: true, state: { notice: EXPIRED_SESSION_NOTICE } });
  }
};

const useCompleteLogout = () => {
  const { resetState } = useSelectedUserConfigSlice();
  const privySession = usePrivySession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { add: addToast } = useToast();

  return useCallback(
    async (mode: LogoutMode): Promise<LogoutResult> => {
      const backendSignedOut = await signOutEverywhere(privySession);

      if (!backendSignedOut && mode !== 'reauth') {
        return failLogout(mode, addToast);
      }

      await clearLocalSession(queryClient, resetState);
      leaveAfterLogout(mode, navigate);
      console.log('[Logout] Logout process complete');
      return { status: 'complete', backendSignedOut };
    },
    [addToast, navigate, privySession, queryClient, resetState],
  );
};

export const useLogout = (): UseLogoutReturn => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { add: addToast } = useToast();
  const completeLogout = useCompleteLogout();

  const logout = useCallback(
    async ({ mode = 'normal' }: { mode?: LogoutMode } = {}): Promise<LogoutResult> => {
      if (isLoggingOut) {
        console.log('[Logout] Already logging out, skipping');
        return IN_PROGRESS;
      }

      beginLogout();
      setIsLoggingOut(true);

      try {
        return await completeLogout(mode);
      } catch {
        console.error('[Logout] Unexpected error during logout');
        return failLogout(mode, addToast);
      } finally {
        setIsLoggingOut(false);
      }
    },
    [addToast, completeLogout, isLoggingOut],
  );

  return { logout, isLoggingOut };
};
